import { CloseOutlined, DownOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import {
  EventNotificationFrequencyWeekly,
  FishingReport,
  OmniaPagination,
  Species,
  WaterbodyDetail,
} from '@omniafishing/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl';
import { useSelector } from 'react-redux';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { isDone, LoadingState } from '../../../../constants/loading_state';
import { QueryKeys } from '../../../../constants/query_keys';
import useDynamicRefs from '../../../../hooks/use_dynamic_refs';
import { useQueryString } from '../../../../hooks/use_query_string';
import { useResponsive } from '../../../../hooks/use_responsive';
import { useUserWaterbodies } from '../../../../hooks/use_user_waterbodies';
import { useWaterbodySpeciesSurveyData } from '../../../../hooks/use_waterbody_species_survery_data';
import { useWaterbodyTechniques } from '../../../../hooks/use_waterbody_techniques';
import { useWaterbodyTopSpecies } from '../../../../hooks/use_waterbody_top_species';
import { apiV1 } from '../../../../lib/api';
import { FIVE_MINUTES_IN_MS } from '../../../../lib/time';
import { OmniaUrls } from '../../../../lib/urls';
import { getSpeciesBySpeciesName } from '../../../../redux/reference_data';
import {
  ActiveMapPageTab,
  ExtendedMapQueryParams,
} from '../../../../routes/map_page/lib/map_page_types';
import { useSpeciesSeasonGroupFilters } from '../../../../routes/map_page/lib/use_species_season_group_filters';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { OmniaLinkButton } from '../../../omnia_button/omnia_link_button';
import SignupPopover from '../../../signup_popover/signup_popover';
import styles from './lakes_modal.less';
import { SpeciesSeasonFilters } from './species_season_filters';
import { TabFishingReports } from './tab_fishing_reports';
import { TabHotbaits } from './tab_hotbaits';
import { TabOverview } from './tab_overview/tab_overview';
import { TabSpecies } from './tab_species/tab_species';
import { TabTechniques } from './tab_techniques';

interface LakesModalDetailScreenProps {
  waterbodyLoadingState: LoadingState;
  waterbody: WaterbodyDetail | null;
  mapRef: MapRef;
  onClose: () => void;
}

interface LakeModalTab {
  name: string;
  panel: JSX.Element;
  queryParam: ActiveMapPageTab;
}

const getTabIndexFromQuery = (tabs: LakeModalTab[], tabQuery: string) => {
  const index = _.findIndex(tabs, (t) => t.queryParam === tabQuery);
  return index === -1 ? 0 : index;
};

export const LakesModalDetailScreen = (props: LakesModalDetailScreenProps) => {
  const { waterbodyLoadingState, waterbody, onClose } = props;

  const { getRef, setRef } = useDynamicRefs();
  const { isMobile } = useResponsive();
  const { isWaterbodyInUserWaterbodies, favoriteWaterbody, unfavoriteWaterbody } =
    useUserWaterbodies();
  const isUserFavorite = isWaterbodyInUserWaterbodies(waterbody);

  const { getCurrentQuery, replaceQueryString } = useQueryString();

  const currentQuery = getCurrentQuery<ExtendedMapQueryParams>();
  const tabQuery = currentQuery.tab;
  const externalSearch = currentQuery.external_search;

  const isCondensedModal = externalSearch && isMobile;
  const [isModalClosed, setIsModalClosed] = useState(isCondensedModal);

  const { filterSeasonGroup, filterSpecies } = useSpeciesSeasonGroupFilters();

  const { waterbodyTopSpecies } = useWaterbodyTopSpecies({
    selectedSeasonGroupName: filterSeasonGroup?.name,
    waterbodyUrlSlug: waterbody?.url_slug,
  });
  const selectedSpeciesName = filterSpecies?.name ?? waterbodyTopSpecies?.name;
  const speciesBySpeciesName = useSelector(getSpeciesBySpeciesName);
  const selectedSpecies = speciesBySpeciesName(selectedSpeciesName);

  const waterbodyFishingReportsQueryParams: Parameters<
    typeof apiV1.waterbodyFishingReportsFetch
  >[1] = {
    species: selectedSpeciesName,
    season_group: filterSeasonGroup?.name,
    sort: 'seasonal_chronological',
    per_page: 6,
  };
  const waterbodyFishingReportsQuery = useInfiniteQuery<{
    fishingReports: FishingReport[];
    totalFishingReportsCount: number;
    metadata: OmniaPagination & {
      count_filtered: number;
      count_total: number;
    };
  }>({
    queryKey: QueryKeys.WATERBODIES.FISHING_REPORTS(
      waterbody?.url_slug,
      waterbodyFishingReportsQueryParams
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      const { metadata } = lastPage;
      const hasMorePages = metadata.count_filtered > pages.length * metadata.per_page;
      return hasMorePages ? lastPage.metadata.page + 1 : undefined;
    },
    queryFn: async (params) => {
      const { pageParam } = params;
      const res = await apiV1.waterbodyFishingReportsFetch(waterbody?.url_slug, {
        ...waterbodyFishingReportsQueryParams,
        page: pageParam as number,
      });
      return {
        fishingReports: res.data.data,
        totalFishingReportsCount: res.data.metadata.count_filtered,
        metadata: res.data.metadata,
      };
    },
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: !!waterbody,
  });

  const fishingReports =
    waterbodyFishingReportsQuery.data?.pages.flatMap((page) => page.fishingReports) || [];
  const totalFishingReportsCount =
    waterbodyFishingReportsQuery.data?.pages[0].totalFishingReportsCount || 0;
  const fishingReportsLoading = waterbodyFishingReportsQuery.isLoading;

  const observer = useRef<IntersectionObserver>();
  const loadMoreElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (waterbodyFishingReportsQuery.isLoading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0]?.isIntersecting &&
          waterbodyFishingReportsQuery.hasNextPage &&
          !waterbodyFishingReportsQuery.isFetching
        ) {
          waterbodyFishingReportsQuery.fetchNextPage();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [
      waterbodyFishingReportsQuery.fetchNextPage,
      waterbodyFishingReportsQuery.hasNextPage,
      waterbodyFishingReportsQuery.isFetching,
      waterbodyFishingReportsQuery.isLoading,
    ]
  );

  useEffect(() => {
    if (!fishingReportsLoading && fishingReports.length === 0) {
      setIsModalClosed(false);
    }
  }, [fishingReports, fishingReportsLoading]);

  const { waterbodyTechniques } = useWaterbodyTechniques({
    waterbodySlug: waterbody?.url_slug,
    selectedSpeciesName,
    selectedSeasonGroupName: filterSeasonGroup?.name,
  });

  const [selectedTechniqueName, setSelectedTechniqueName] = useState<string | null>();
  const selectedTechnique =
    waterbodyTechniques.find((t) => t.name === selectedTechniqueName) || waterbodyTechniques[0];

  const { waterbodySpeciesSurveyData } = useWaterbodySpeciesSurveyData({
    waterbodySlug: waterbody?.url_slug,
  });
  const [selectedSurveySpecies, setSelectedSurveySpecies] = useState<Species | null>(null);

  const contentRef = useRef<HTMLDivElement>();

  const tabs: LakeModalTab[] = [
    {
      name: 'Overview',
      panel: (
        <TabOverview
          waterbody={waterbody}
          key={waterbody?.id}
          waterbodyTechniques={waterbodyTechniques}
          onTechniqueClick={(techniqueName) => {
            const techniqueTabIndex = tabs.findIndex((t) => t.queryParam === 'top_techniques');
            handleTabSelect(techniqueTabIndex);
            setSelectedTechniqueName(techniqueName);
          }}
          selectedSeasonGroup={filterSeasonGroup}
          selectedSpecies={filterSpecies}
          topSpecies={selectedSpecies}
          topSpeciesSurveyData={waterbodySpeciesSurveyData?.species[selectedSpecies?.display_name]}
          onSpeciesClick={(species) => {
            const speciesTabIndex = tabs.findIndex((t) => t.queryParam === 'species');
            setSelectedSurveySpecies(species);
            handleTabSelect(speciesTabIndex);
          }}
          onAllSpeciesClick={() => {
            const speciesTabIndex = tabs.findIndex((t) => t.queryParam === 'species');
            setSelectedSurveySpecies(null);
            handleTabSelect(speciesTabIndex);
          }}
        />
      ),
      queryParam: 'lake_info',
    },
    {
      name: `Fishing Reports${
        !fishingReportsLoading && totalFishingReportsCount > 0
          ? ` (${totalFishingReportsCount})`
          : ''
      }`,
      panel: (
        <TabFishingReports
          waterbody={waterbody}
          fishingReportsLoading={fishingReportsLoading}
          fishingReports={fishingReports}
          selectedSeasonGroup={filterSeasonGroup}
          selectedSpecies={filterSpecies}
          loadMoreElementRef={loadMoreElementRef}
          key={waterbody?.id}
        />
      ),
      queryParam: 'fishing_reports',
    },
    {
      name: 'Hot Baits',
      panel: (
        <TabHotbaits
          waterbody={waterbody}
          key={waterbody?.id}
          selectedSeasonGroup={filterSeasonGroup}
          selectedSpecies={selectedSpecies}
        />
      ),
      queryParam: 'hotbaits',
    },
    {
      name: 'Top Techniques',
      panel: (
        <TabTechniques
          waterbody={waterbody}
          key={waterbody?.id}
          waterbodyTechniques={waterbodyTechniques}
          selectedSpeciesName={selectedSpeciesName}
          selectedTechniqueName={selectedTechnique?.name}
          selectedSeasonGroup={filterSeasonGroup}
          onTechniqueClick={(techniqueName) => {
            setSelectedTechniqueName(techniqueName);
          }}
        />
      ),
      queryParam: 'top_techniques',
    },
  ];

  if (waterbodySpeciesSurveyData != null) {
    // Find the desired index if order matters, e.g., before 'Top Techniques'
    const techniquesIndex = tabs.findIndex((tab) => tab.queryParam === 'top_techniques');
    const insertionIndex = techniquesIndex !== -1 ? techniquesIndex : tabs.length; // Insert before techniques or at the end

    tabs.splice(insertionIndex, 0, {
      // Use splice to insert at a specific position
      name: 'Species',
      panel: (
        <TabSpecies
          waterbodySpeciesSurveyData={waterbodySpeciesSurveyData}
          topSpecies={waterbodyTopSpecies}
          selectedSpecies={selectedSurveySpecies}
          onSpeciesSelect={(species) => {
            setSelectedSurveySpecies(species);
          }}
        />
      ),
      queryParam: 'species',
    });
  }

  const tabsWrapperRef = useRef(null);
  const [tabIndex, setTabIndex] = useState(getTabIndexFromQuery(tabs, tabQuery));
  const handleTabSelect = (index: number, params?: Partial<ExtendedMapQueryParams>) => {
    setTabIndex(index);
    const paramsToSet = {
      tab: tabs[index].queryParam,
    };
    if (params) {
      Object.assign(paramsToSet, params);
    }
    replaceQueryString<ExtendedMapQueryParams>(paramsToSet);
    // TODO: on first load if a tab is selected it is not scrolled to, no idea how to fix this
    scrollToTab(index);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [selectedSurveySpecies]);

  const scrollToTab = (index: number) => {
    const tabElement = getRef<HTMLSpanElement>(tabs[index].queryParam)?.current;
    const wrapperEl = tabsWrapperRef.current?.firstChild;
    if (wrapperEl && tabElement) {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const tabRect = tabElement.getBoundingClientRect();
      const relativeLeft = tabRect.left - wrapperRect.left;
      wrapperEl.scrollTo({
        left: relativeLeft + wrapperEl.scrollLeft - 16,
        behavior: 'smooth',
      });
    }
  };

  const onFollowClick = () => {
    if (isUserFavorite) {
      unfavoriteWaterbody.mutate(waterbody.url_slug);
    } else {
      favoriteWaterbody.mutate({
        event_notifications: {
          frequency: EventNotificationFrequencyWeekly,
          new_fishing_report: true,
          new_article: true,
        },
        url_slug: waterbody.url_slug,
      });
    }
  };

  return (
    <>
      <div className={classNames(styles.modalHeader, styles.modalHeader__detail)}>
        <div className={styles.detailHeaderTop}>
          <span className={styles.heading}>
            <span className={styles.detailHeaderName}>
              {waterbody?.primary_name || <>&nbsp;</>}
            </span>
          </span>
          <CloseOutlined onClick={onClose} />
        </div>
      </div>
      <div
        className={classNames(styles.modalContent, {
          [styles.modalContent__condensed]: isModalClosed,
        })}
        ref={contentRef}
      >
        {isDone(waterbodyLoadingState) && waterbody && (
          <>
            <Tabs
              selectedTabClassName={styles.tab__active}
              selectedIndex={tabIndex}
              onSelect={(index) => {
                handleTabSelect(index);
              }}
              domRef={(node) => {
                tabsWrapperRef.current = node;
              }}
            >
              <TabList className={styles.tabs}>
                {tabs.map((tab) => {
                  return (
                    <Tab key={tab.name} className={styles.tab}>
                      <span className={styles.tabHeading} ref={setRef(tab.queryParam)}>
                        {tab.name}
                      </span>
                    </Tab>
                  );
                })}
              </TabList>
              <div>
                <SpeciesSeasonFilters
                  selectedSpecies={filterSpecies}
                  selectedSeasonGroup={filterSeasonGroup}
                  speciesOptions={waterbody.species}
                  onSpeciesSelect={(s) => {
                    replaceQueryString<ExtendedMapQueryParams>({
                      species: s,
                    });
                  }}
                  onSeasonGroupSelect={(sg) => {
                    replaceQueryString<ExtendedMapQueryParams>({
                      season_group: sg,
                    });
                  }}
                />
              </div>
              {tabs.map((tab) => (
                <TabPanel key={tab.name} className={styles.tabContent}>
                  {tab.panel}
                </TabPanel>
              ))}
            </Tabs>
          </>
        )}
      </div>
      <footer
        className={classNames(styles.modalFooter, {
          [styles.modalFooter__small]: isModalClosed,
        })}
      >
        {isCondensedModal && isModalClosed && (
          <OmniaButton
            kind="tertiary"
            size="md"
            shape="circle"
            className={styles.moreButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsModalClosed(false);
            }}
          >
            <DownOutlined />
          </OmniaButton>
        )}
        {isModalClosed ? null : (
          <SignupPopover actionText="follow lakes">
            <OmniaButton size={'md'} kind="tertiary" onPress={onFollowClick}>
              {isUserFavorite ? (
                <>
                  <StarFilled /> Followed
                </>
              ) : (
                <>
                  <StarOutlined /> Follow
                </>
              )}
            </OmniaButton>
          </SignupPopover>
        )}

        <OmniaLinkButton
          size={'md'}
          kind="primary"
          to={waterbody ? OmniaUrls.waterbody(waterbody) : '#'}
        >
          View Full Lake Details
        </OmniaLinkButton>
      </footer>
    </>
  );
};
